import React, { useState, useEffect, useContext, useMemo } from "react";
import Avatar from "@mui/material/Avatar";
import { useNavigate } from "react-router-dom";
import userContext from "../../context/UserContext";
import { useDarkMode } from "../../context/darkModeContext";
import toast, { Toaster } from "react-hot-toast";
import { api } from "../../utils/axios-instance";
import { useDepartment } from "../../stores/departmentStore";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";

/* ── Icons ── */
const PencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      d="M9.5 2L12 4.5L4.5 12H2v-2.5L9.5 2z"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
  </svg>
);
const CloseIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <line
      x1="3"
      y1="3"
      x2="10"
      y2="10"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <line
      x1="10"
      y1="3"
      x2="3"
      y2="10"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);
const ColumnsIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <rect
      x="1"
      y="1.5"
      width="3"
      height="10"
      rx="0.8"
      stroke="currentColor"
      strokeWidth="1.2"
    />
    <rect
      x="5"
      y="1.5"
      width="3"
      height="10"
      rx="0.8"
      stroke="currentColor"
      strokeWidth="1.2"
    />
    <rect
      x="9"
      y="1.5"
      width="3"
      height="10"
      rx="0.8"
      stroke="currentColor"
      strokeWidth="1.2"
    />
  </svg>
);
const FilterIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <path
      d="M1 2h11l-4 5v4l-3 1V7L1 2z"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
  </svg>
);
const DensityIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <line
      x1="1.5"
      y1="3"
      x2="11.5"
      y2="3"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <line
      x1="1.5"
      y1="6.5"
      x2="11.5"
      y2="6.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <line
      x1="1.5"
      y1="10"
      x2="11.5"
      y2="10"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);
const DownloadIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <path
      d="M6.5 2v7M4 6.5l2.5 2.5L9 6.5"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2 11h9"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
    />
  </svg>
);

const ROW_DENSITIES = {
  comfortable: { padding: "14px 16px", fontSize: 13.5 },
  standard: { padding: "10px 16px", fontSize: 13 },
  compact: { padding: "6px 16px", fontSize: 12.5 },
};

const FALLBACK_COLOR = "#6366f1";

export default function AdministratorAccounts({ activeDept }) {
  const context = useContext(userContext);
  const { darkMode } = useDarkMode();
  const {
    setUsers,
    filteredUsers,
    setFilteredUsers,
    userTypes,
    setUserTypes,
    activeType,
    setActiveType,
    applyFilter,
  } = context;

  const navigate = useNavigate();
  const departments = useDepartment((state) => state.departments);
  const [refetch, setRefetch] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteUser, setPendingDeleteUser] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Table UI state
  const [page, setPage] = useState(1);
  const pageSize = 11;
  const [showColumns, setShowColumns] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showDensity, setShowDensity] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [density, setDensity] = useState("standard");
  const [visibleCols, setVisibleCols] = useState({
    name: true,
    department: true,
    role: true,
    status: true,
    edit: true,
    remove: true,
  });

  const deptMap = useMemo(() => {
    return (departments || []).reduce((acc, dept) => {
      acc[dept.id] = dept;
      return acc;
    }, {});
  }, [departments]);

  const getDeptLabel = (user) => {
    if (!user) return "—";
    if (user.dept === "All") return "All";
    if (Array.isArray(user.dept_names) && user.dept_names.length)
      return user.dept_names.join(", ");
    if (Array.isArray(user.dept_ids) && user.dept_ids.length) {
      const names = user.dept_ids
        .map((id) => deptMap[id]?.name)
        .filter(Boolean);
      return names.length ? names.join(", ") : user.dept_ids.join(", ");
    }
    if (user.dept && deptMap[user.dept]) return deptMap[user.dept].name;
    return user.dept || "—";
  };

  const getDeptColor = (user) => {
    if (Array.isArray(user.dept_ids) && user.dept_ids.length)
      return deptMap[user.dept_ids[0]]?.metadata?.bg || FALLBACK_COLOR;
    if (user.dept && deptMap[user.dept])
      return deptMap[user.dept]?.metadata?.bg || FALLBACK_COLOR;
    return FALLBACK_COLOR;
  };

  const normalizeUsers = (users) =>
    (users || []).map((u) => ({
      ...u,
      dept_display: getDeptLabel(u),
      dept_color: getDeptColor(u),
      is_active_display: Boolean(u?.is_active ?? u?.flags?.is_active),
    }));

  const listUsers = async () => {
    try {
      const response = await api.get(`/users/list_users`);
      const filtered = (response.data || []).filter(
        (u) => u.status === "onboarded" && u.flags?.is_approved === true
      );
      const updatedUsers = normalizeUsers(filtered);
      setUsers(updatedUsers);

      const types = ["all", ...new Set(updatedUsers.map((i) => i.role_priv))];

      setUserTypes(types);

      setActiveType((prev) => {
        const currentType = prev || "all";

        if (currentType === "all") {
          setFilteredUsers(updatedUsers);
        } else {
          setFilteredUsers(
            updatedUsers.filter((u) => u.role_priv === currentType)
          );
        }

        return currentType;
      });
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    let interval;
    if (refetch) listUsers().finally(() => setRefetch(false));
    interval = setInterval(listUsers, 3000);
    return () => clearInterval(interval);
  }, [refetch]);

  useEffect(() => {
    if (!Array.isArray(filteredUsers) || filteredUsers.length === 0) return;
    setFilteredUsers(normalizeUsers(filteredUsers));
  }, [deptMap]);

  // Apply all filters
  const displayRows = useMemo(() => {
    return (filteredUsers || [])
      .filter(
        (u) =>
          !filterStatus ||
          (filterStatus === "Online"
            ? u.is_active_display
            : !u.is_active_display)
      )
      .filter((u) => {
        if (!activeDept) return true;

        if (
          u.dept === "All" ||
          u.role_priv === "org_admin" ||
          u.role_priv === "super_admin"
        ) {
          return true;
        }

        if (Array.isArray(u.dept_names) && u.dept_names.includes(activeDept)) {
          return true;
        }

        return false;
      });
  }, [filteredUsers, filterStatus, activeDept]);

  const totalPages = Math.max(1, Math.ceil(displayRows.length / pageSize));
  const pageStart = (page - 1) * pageSize;
  const pageRows = displayRows.slice(pageStart, pageStart + pageSize);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [activeType, activeDept, filterStatus]);

  const handleEditClick = (user) => navigate(`/user-profile/${user.id}`);

  const requestRemoveUser = (user) => {
    setPendingDeleteUser(user);
    setConfirmOpen(true);
  };
  const closeConfirm = () => {
    if (deleting) return;
    setConfirmOpen(false);
    setPendingDeleteUser(null);
  };
  const confirmRemoveUser = async () => {
    if (!pendingDeleteUser?.id) return;
    setDeleting(true);
    try {
      await api.delete(`/users/deleteUser/${pendingDeleteUser.id}/`);
      toast.success("User deleted successfully.");
      setRefetch(true);
      setConfirmOpen(false);
      setPendingDeleteUser(null);
    } catch (error) {
      toast.error("Something went wrong.");
    } finally {
      setDeleting(false);
    }
  };

  const rowStyle = ROW_DENSITIES[density];

  const pageBtnStyle = {
    width: 28,
    height: 28,
    borderRadius: 6,
    background: darkMode ? "#111827" : "#fff",
    border: darkMode ? "1px solid #374151" : "none",
    cursor: "pointer",
    color: "#6b7280",
    fontSize: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const popoverStyle = {
    position: "absolute",
    top: "calc(100% + 6px)",
    left: 0,
    background: darkMode ? "#111827" : "#fff",
    border: darkMode ? "1px solid #374151" : "none",
    borderRadius: 10,
    boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
    zIndex: 100,
    padding: 10,
    minWidth: 160,
  };

  const actionBtnStyle = {
    width: 30,
    height: 30,
    borderRadius: 7,
    background: "none",
    cursor: "pointer",
    border: darkMode ? "1px solid #374151" : "1px solid #e5e7eb",

    color: darkMode ? "#d1d5db" : "#6b7280",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.12s, color 0.12s, border-color 0.12s",
  };

  // Toolbar button style
  const toolbarBtn = (active = false) => ({
    display: "flex",
    alignItems: "center",
    gap: 5,
    padding: "5px 10px",
    border: "none",
    borderRadius: 6,
    background: active ? "#eef2ff" : "none",
    color: active ? "#4f46e5" : darkMode ? "#d1d5db" : "#6b7280",
    fontSize: 11.5,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
    letterSpacing: "0.04em",
    transition: "background 0.12s, color 0.12s",
  });

  const closePopovers = () => {
    setShowColumns(false);
    setShowFilters(false);
    setShowDensity(false);
  };

  return (
    <>
      <Toaster position="bottom-left" reverseOrder={false} />

      {/* Role tabs */}
      <div
        style={{
          display: "flex",
          gap: 4,
          borderBottom: darkMode ? "1px solid #374151" : "1px solid #f1f5f9",
          marginBottom: 2,
        }}
      >
        {(userTypes || []).map((type) => (
          <button
            key={type}
            onClick={() => applyFilter(type)}
            style={{
              padding: "8px 14px",
              background: "none",
              border: "none",
              borderBottom: `2px solid ${activeType === type ? "#4f46e5" : "transparent"}`,
              color: activeType === type ? "#4f46e5" : "#6b7280",
              fontWeight: activeType === type ? 600 : 400,
              fontSize: 13,
              cursor: "pointer",
              fontFamily: "inherit",
              textTransform: "capitalize",
              transition: "color 0.12s",
              marginBottom: -1,
            }}
          >
            {type}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          padding: "8px 0",
          borderBottom: darkMode ? "1px solid #374151" : "1px solid #f1f5f9",
          position: "relative",
        }}
      >
        {/* COLUMNS */}
        <div style={{ position: "relative" }}>
          <button
            style={toolbarBtn(showColumns)}
            onClick={() => {
              setShowColumns((v) => !v);
              setShowFilters(false);
              setShowDensity(false);
            }}
          >
            <ColumnsIcon /> COLUMNS
          </button>
          {showColumns && (
            <div style={popoverStyle}>
              {Object.keys(visibleCols).map((c) => (
                <label key={c} style={popoverItemStyle}>
                  <input
                    type="checkbox"
                    checked={visibleCols[c]}
                    onChange={() =>
                      setVisibleCols((prev) => ({ ...prev, [c]: !prev[c] }))
                    }
                  />
                  <span style={{ textTransform: "capitalize" }}>{c}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* FILTERS */}
        <div style={{ position: "relative" }}>
          <button
            style={toolbarBtn(showFilters || !!filterStatus)}
            onClick={() => {
              setShowFilters((v) => !v);
              setShowColumns(false);
              setShowDensity(false);
            }}
          >
            <FilterIcon /> FILTERS{filterStatus && " •"}
          </button>
          {showFilters && (
            <div style={{ ...popoverStyle, width: 200 }}>
              <label style={popoverLabelStyle}>Status</label>
              <select
                style={popoverSelectStyle}
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">All statuses</option>
                <option value="Online">Online</option>
                <option value="Offline">Offline</option>
              </select>
              <button
                style={popoverClearStyle}
                onClick={() => setFilterStatus("")}
              >
                Clear
              </button>
            </div>
          )}
        </div>

        {/* DENSITY */}
        <div style={{ position: "relative" }}>
          <button
            style={toolbarBtn(showDensity)}
            onClick={() => {
              setShowDensity((v) => !v);
              setShowColumns(false);
              setShowFilters(false);
            }}
          >
            <DensityIcon /> DENSITY
          </button>
          {showDensity && (
            <div style={popoverStyle}>
              {Object.keys(ROW_DENSITIES).map((d) => (
                <button
                  key={d}
                  style={{
                    ...popoverItemStyle,
                    color: density === d ? "#4f46e5" : "#374151",
                    fontWeight: density === d ? 600 : 400,
                    background: density === d ? "#eef2ff" : "none",
                    border: "none",
                    cursor: "pointer",
                    borderRadius: 6,
                    fontFamily: "inherit",
                    width: "100%",
                    textAlign: "left",
                  }}
                  onClick={() => {
                    setDensity(d);
                    setShowDensity(false);
                  }}
                >
                  <span style={{ textTransform: "capitalize" }}>{d}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* EXPORT */}
        <button
          style={toolbarBtn()}
          onClick={() => {
            const header = "Name,Department,Role,Status\n";
            const csv =
              header +
              displayRows
                .map(
                  (u) =>
                    `"${u.name} ${u.last_name || ""}","${u.dept_display}","${u.role_priv}","${u.is_active_display ? "Online" : "Offline"}"`
                )
                .join("\n");
            const blob = new Blob([csv], { type: "text/csv" });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = "users.csv";
            a.click();
          }}
        >
          <DownloadIcon /> EXPORT
        </button>
      </div>

      {/* Table */}
      <div
        style={{
          border: darkMode ? "1px solid #374151" : "1px solid #f1f5f9",
          borderRadius: 10,
          overflow: "hidden",
          marginTop: 2,
        }}
      >
        {/* Head */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: buildGridCols(visibleCols),
            background: darkMode ? "#0f172a" : "#fafbfc",
            borderBottom: darkMode ? "1px solid #374151" : "1px solid #f1f5f9",
          }}
        >
          {visibleCols.name && <div style={thStyle}>Name</div>}
          {visibleCols.department && <div style={thStyle}>Department</div>}
          {visibleCols.role && <div style={thStyle}>Roles</div>}
          {visibleCols.status && <div style={thStyle}>Status</div>}
          {visibleCols.edit && (
            <div style={{ ...thStyle, textAlign: "center" }}>Edit</div>
          )}
          {visibleCols.remove && (
            <div style={{ ...thStyle, textAlign: "center" }}>Remove</div>
          )}
        </div>

        {/* Body */}
        {pageRows.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px 16px",
              color: "#9ca3af",
              fontSize: 13,
            }}
          >
            No users match the current filters.
          </div>
        ) : (
          pageRows.map((u) => (
            <div
              key={u.id || u.email}
              style={{
                display: "grid",
                gridTemplateColumns: buildGridCols(visibleCols),
                borderBottom: darkMode
                  ? "1px solid #1f2937"
                  : "1px solid #f9fafb",
                padding: rowStyle.padding,
                fontSize: rowStyle.fontSize,
                alignItems: "center",
                transition: "background 0.1s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = darkMode
                  ? "#1f2937"
                  : "#fafbff")
              }
              onMouseLeave={(e) => (e.currentTarget.style.background = "")}
            >
              {visibleCols.name && (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Avatar
                    src={u.profilePictureUrl}
                    alt={u.name}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      flexShrink: 0,
                    }}
                    variant="rounded"
                  />
                  <span
                    style={{
                      fontWeight: 500,
                      color: darkMode ? "#f9fafb" : "#111827",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {u.name} {u.last_name || ""}
                  </span>
                </div>
              )}
              {visibleCols.department && (
                <div>
                  {u.dept_display && u.dept_display !== "—" ? (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        background: (u.dept_color || FALLBACK_COLOR) + "20",
                        color: u.dept_color || FALLBACK_COLOR,
                        padding: "3px 10px",
                        borderRadius: 20,
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      {u.dept_display}
                    </span>
                  ) : (
                    "—"
                  )}
                </div>
              )}
              {visibleCols.role && (
                <div style={{ color: darkMode ? "#d1d5db" : "#374151" }}>
                  {u.role_priv}
                </div>
              )}
              {visibleCols.status && (
                <div>
                  <span
                    style={{
                      padding: "3px 12px",
                      borderRadius: 20,
                      fontSize: 12,
                      fontWeight: 500,
                      background: u.is_active_display ? "#ecfdf3" : "#fef2f2",
                      color: u.is_active_display ? "#16a34a" : "#dc2626",
                    }}
                  >
                    {u.is_active_display ? "Online" : "Offline"}
                  </span>
                </div>
              )}
              {visibleCols.edit && (
                <div style={{ textAlign: "center" }}>
                  <button
                    onClick={() => handleEditClick(u)}
                    style={actionBtnStyle}
                  >
                    <PencilIcon />
                  </button>
                </div>
              )}
              {visibleCols.remove && (
                <div style={{ textAlign: "center" }}>
                  <button
                    onClick={() => requestRemoveUser(u)}
                    style={{ ...actionBtnStyle, color: "#9ca3af" }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#fef2f2";
                      e.currentTarget.style.color = "#ef4444";
                      e.currentTarget.style.borderColor = "#fca5a5";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "";
                      e.currentTarget.style.color = "#9ca3af";
                      e.currentTarget.style.borderColor = "#e5e7eb";
                    }}
                  >
                    <CloseIcon />
                  </button>
                </div>
              )}
            </div>
          ))
        )}

        {/* Pagination */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 8,
            padding: "10px 16px",
            borderTop: darkMode ? "1px solid #374151" : "1px solid #f1f5f9",
            background: darkMode ? "#0f172a" : "#fafbfc",
          }}
        >
          <span
            style={{
              fontSize: 12,
              color: darkMode ? "#9ca3af" : "#6b7280",
              marginRight: 8,
            }}
          >
            {displayRows.length === 0
              ? "0"
              : `${pageStart + 1}–${Math.min(pageStart + pageSize, displayRows.length)}`}{" "}
            of {displayRows.length}
          </span>
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            style={pageBtnStyle}
          >
            ‹
          </button>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            style={pageBtnStyle}
          >
            ›
          </button>
        </div>
      </div>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmOpen} onClose={closeConfirm} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold" }}>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mt: 1 }}>
            Are you sure you want to delete{" "}
            <strong>
              {pendingDeleteUser?.name} {pendingDeleteUser?.last_name}
            </strong>
            ?
            <br />
            <br />
            This action is permanent — you can't restore the data after.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ pb: 2, pr: 3 }}>
          <Button onClick={closeConfirm} variant="outlined" disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={confirmRemoveUser}
            variant="contained"
            color="error"
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete Permanently"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/* ── Grid columns helper ── */
function buildGridCols(visibleCols) {
  const cols = [];
  if (visibleCols.name) cols.push("minmax(180px, 2fr)");
  if (visibleCols.department) cols.push("minmax(130px, 1.5fr)");
  if (visibleCols.role) cols.push("minmax(100px, 1fr)");
  if (visibleCols.status) cols.push("100px");
  if (visibleCols.edit) cols.push("60px");
  if (visibleCols.remove) cols.push("70px");
  return cols.join(" ");
}

/* ── Shared inline styles ── */
const thStyle = {
  padding: "10px 16px",
  fontSize: 11.5,
  fontWeight: 600,
  color: "#9ca3af",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
};

const popoverItemStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "6px 8px",
  fontSize: 13,
  color: "#374151",
  cursor: "pointer",
  borderRadius: 6,
};

const popoverLabelStyle = {
  display: "block",
  fontSize: 10.5,
  fontWeight: 700,
  color: "#9ca3af",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  marginBottom: 6,
  padding: "0 4px",
};

const popoverSelectStyle = {
  width: "100%",
  padding: "6px 8px",
  borderRadius: 7,
  border: "1px solid #e5e7eb",
  fontSize: 13,
  fontFamily: "inherit",
  color: "#374151",
  outline: "none",
  marginBottom: 10,
};

const popoverClearStyle = {
  width: "100%",
  padding: "6px 8px",
  borderRadius: 7,
  border: "1px solid #e5e7eb",
  fontSize: 12.5,
  fontFamily: "inherit",
  color: "#6b7280",
  cursor: "pointer",
  background: "#fff",
  transition: "background 0.12s",
};
